/**
 * Non-blocking "Did you mean" for common email typos that bounce
 * (Postmark, Mailgun, Mailcheck-style). Never treats a suggestion as
 * required — callers must always let the original address through.
 */

export type EmailSuggestion = {
  /** Trimmed address as typed (local-part case preserved). */
  original: string;
  suggested: string;
  originalDomain: string;
  suggestedDomain: string;
};

export type SuggestEmailOptions = {
  /**
   * True once the user has left the field or reached the summary.
   * While typing we suppress suggestions that look like a prefix of
   * the intended domain (e.g. `gmail.co` → `gmail.com`).
   */
  committed?: boolean;
};

/** Known consumer / ISP domains — never "correct" these (except soft hints). */
const PROVIDERS: Record<string, { canonical: string; tlds: string[] }> = {
  gmail: { canonical: "gmail.com", tlds: ["com"] },
  googlemail: { canonical: "googlemail.com", tlds: ["com"] },
  hotmail: {
    canonical: "hotmail.com",
    tlds: ["com", "co.uk", "ie", "fr", "de", "es", "it", "ca"],
  },
  outlook: { canonical: "outlook.com", tlds: ["com", "ie", "co.uk"] },
  yahoo: {
    canonical: "yahoo.com",
    tlds: ["com", "co.uk", "ie", "fr", "de", "es", "it", "ca", "com.au"],
  },
  icloud: { canonical: "icloud.com", tlds: ["com"] },
  me: { canonical: "me.com", tlds: ["com"] },
  mac: { canonical: "mac.com", tlds: ["com"] },
  live: { canonical: "live.com", tlds: ["com", "ie", "co.uk"] },
  msn: { canonical: "msn.com", tlds: ["com"] },
  aol: { canonical: "aol.com", tlds: ["com", "co.uk"] },
  protonmail: { canonical: "protonmail.com", tlds: ["com"] },
  proton: { canonical: "proton.me", tlds: ["me"] },
  pm: { canonical: "pm.me", tlds: ["me"] },
  gmx: { canonical: "gmx.com", tlds: ["com", "de", "net"] },
  fastmail: { canonical: "fastmail.com", tlds: ["com"] },
  zoho: { canonical: "zoho.com", tlds: ["com"] },
  yandex: { canonical: "yandex.com", tlds: ["com", "ru"] },
  hey: { canonical: "hey.com", tlds: ["com"] },
  btinternet: { canonical: "btinternet.com", tlds: ["com"] },
  virginmedia: { canonical: "virginmedia.com", tlds: ["com"] },
  sky: { canonical: "sky.com", tlds: ["com"] },
  eircom: { canonical: "eircom.net", tlds: ["net"] },
  eir: { canonical: "eir.ie", tlds: ["ie"] },
  ntlworld: { canonical: "ntlworld.com", tlds: ["com"] },
  talktalk: { canonical: "talktalk.net", tlds: ["net"] },
  blueyonder: { canonical: "blueyonder.co.uk", tlds: ["co.uk"] },
  btopenworld: { canonical: "btopenworld.com", tlds: ["com"] },
};

/**
 * Valid domains that are often a slip for a much more common provider.
 * Suggestion only — mail.com and google.com are real.
 */
const SOFT_HINTS: Record<string, string> = {
  "mail.com": "gmail.com",
  "google.com": "gmail.com",
};

/**
 * Full-domain typos seen in bounces. Keys are lowercase.
 * These win over fuzzy matching.
 */
const DOMAIN_TYPOS: Record<string, string> = {
  // Gmail
  "gmial.com": "gmail.com",
  "gmal.com": "gmail.com",
  "gamil.com": "gmail.com",
  "gnail.com": "gmail.com",
  "gmaill.com": "gmail.com",
  "gmil.com": "gmail.com",
  "gmsil.com": "gmail.com",
  "gmaul.com": "gmail.com",
  "gmaol.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gmeil.com": "gmail.com",
  "gimal.com": "gmail.com",
  "gmaik.com": "gmail.com",
  "gmael.com": "gmail.com",
  "hmail.com": "gmail.com",
  "gmail.con": "gmail.com",
  "gmail.ocm": "gmail.com",
  "gmail.cmo": "gmail.com",
  "gmail.cpm": "gmail.com",
  "gmail.cm": "gmail.com",
  "gmail.om": "gmail.com",
  "gmail.vom": "gmail.com",
  "gmail.xom": "gmail.com",
  "gmail.cim": "gmail.com",
  "gmail.colm": "gmail.com",
  "gmail.comn": "gmail.com",
  "gmail.comm": "gmail.com",
  "gmail.coom": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.net": "gmail.com",
  "gmail.org": "gmail.com",
  "gmail.ie": "gmail.com",
  "gmail.co.uk": "gmail.com",
  "gmail.uk": "gmail.com",
  "gmail.de": "gmail.com",
  "gmail.fr": "gmail.com",
  "gmail.eu": "gmail.com",
  "gmail.com.com": "gmail.com",
  gmailcom: "gmail.com",
  "gmailc.om": "gmail.com",

  // Hotmail
  "hotmial.com": "hotmail.com",
  "hotmal.com": "hotmail.com",
  "hotamil.com": "hotmail.com",
  "hotnail.com": "hotmail.com",
  "hitmail.com": "hotmail.com",
  "hormail.com": "hotmail.com",
  "hotmaill.com": "hotmail.com",
  "htomail.com": "hotmail.com",
  "hotmai.com": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "hotmail.ocm": "hotmail.com",
  "hotmail.cmo": "hotmail.com",
  "hotmail.cm": "hotmail.com",
  "hotmail.co": "hotmail.com",
  hotmailcom: "hotmail.com",

  // Outlook
  "outllok.com": "outlook.com",
  "outlok.com": "outlook.com",
  "outook.com": "outlook.com",
  "outloo.com": "outlook.com",
  "outlokk.com": "outlook.com",
  "outllook.com": "outlook.com",
  "outlook.con": "outlook.com",
  "outlook.ocm": "outlook.com",
  "outlook.cmo": "outlook.com",
  "outlook.co": "outlook.com",
  outlookcom: "outlook.com",

  // Yahoo
  "yaho.com": "yahoo.com",
  "yahooo.com": "yahoo.com",
  "yahho.com": "yahoo.com",
  "yhaoo.com": "yahoo.com",
  "yhoo.com": "yahoo.com",
  "yahoo.con": "yahoo.com",
  "yahoo.ocm": "yahoo.com",
  "yaho.co.uk": "yahoo.co.uk",
  yahoocom: "yahoo.com",

  // iCloud
  "iclod.com": "icloud.com",
  "icould.com": "icloud.com",
  "icluod.com": "icloud.com",
  "iclould.com": "icloud.com",
  "iclud.com": "icloud.com",
  "icloud.con": "icloud.com",
  "icloud.ocm": "icloud.com",
  icloudcom: "icloud.com",

  // Other providers
  "live.con": "live.com",
  "live.ocm": "live.com",
  "msn.con": "msn.com",
  "aol.con": "aol.com",
  "aol.ocm": "aol.com",
  "protonmail.con": "protonmail.com",
  "protonmai.com": "protonmail.com",
};

/** Second-level typos (gmail / hotmail / …). */
const SLD_TYPOS: Record<string, string> = {
  gmial: "gmail",
  gmal: "gmail",
  gamil: "gmail",
  gnail: "gmail",
  gmaill: "gmail",
  gmil: "gmail",
  gmsil: "gmail",
  gmaul: "gmail",
  gmaol: "gmail",
  gmai: "gmail",
  gmeil: "gmail",
  gimal: "gmail",
  gmaik: "gmail",
  gmael: "gmail",
  hmail: "gmail",
  hotmial: "hotmail",
  hotmal: "hotmail",
  hotamil: "hotmail",
  hotnail: "hotmail",
  hitmail: "hotmail",
  hormail: "hotmail",
  hotmaill: "hotmail",
  htomail: "hotmail",
  hotmai: "hotmail",
  outllok: "outlook",
  outlok: "outlook",
  outook: "outlook",
  outloo: "outlook",
  outlokk: "outlook",
  outllook: "outlook",
  yaho: "yahoo",
  yahooo: "yahoo",
  yahho: "yahoo",
  yhaoo: "yahoo",
  yhoo: "yahoo",
  iclod: "icloud",
  icould: "icloud",
  icluod: "icloud",
  iclould: "icloud",
  iclud: "icloud",
};

/**
 * TLDs that are not real (or never used as email TLDs here).
 * Always safe to rewrite, even on unknown company domains.
 */
const TLD_TYPOS: Record<string, string> = {
  ocm: "com",
  cmo: "com",
  con: "com",
  cpm: "com",
  vom: "com",
  xom: "com",
  comm: "com",
  coom: "com",
  colm: "com",
  comn: "com",
  cim: "com",
  clm: "com",
  coml: "com",
  comk: "com",
  c0m: "com",
  nte: "net",
  ney: "net",
  ogr: "org",
  orgg: "org",
};

const COMPOUND_TLDS = [
  "co.uk",
  "com.au",
  "co.nz",
  "co.ie",
  "org.uk",
  "ac.uk",
];

const KNOWN_DOMAINS: Set<string> = new Set();
const KNOWN_SLDS: string[] = [];
const CANONICAL_BY_SLD: Record<string, string> = {};
const TLDS_BY_SLD: Record<string, Set<string>> = {};

for (const [sld, spec] of Object.entries(PROVIDERS)) {
  CANONICAL_BY_SLD[sld] = spec.canonical;
  TLDS_BY_SLD[sld] = new Set(spec.tlds);
  KNOWN_SLDS.push(sld);
  KNOWN_DOMAINS.add(spec.canonical);
  for (const tld of spec.tlds) {
    KNOWN_DOMAINS.add(`${sld}.${tld}`);
  }
}
// Soft-hint sources are real addresses — do not fuzzy-match away from them.
KNOWN_DOMAINS.add("mail.com");
KNOWN_DOMAINS.add("google.com");

function splitDomain(domain: string): { sld: string; tld: string } {
  for (const tld of COMPOUND_TLDS) {
    const suffix = `.${tld}`;
    if (domain.endsWith(suffix) && domain.length > suffix.length) {
      return { sld: domain.slice(0, -suffix.length), tld };
    }
  }
  const i = domain.lastIndexOf(".");
  if (i <= 0) return { sld: domain, tld: "" };
  return { sld: domain.slice(0, i), tld: domain.slice(i + 1) };
}

/** Strip paste/iOS junk. Does not change letter case. */
export function sanitizeEmailInput(raw: string): string {
  if (!raw) return "";
  return raw
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\u00a0/g, " ")
    .trim()
    .replace(/\s+/g, "");
}

function cleanDomainKeepCase(raw: string): string {
  return raw
    .replace(/,/g, ".")
    .replace(/\.+/g, ".")
    .replace(/^\.+|\.+$/g, "");
}

function isLetter(ch: string): boolean {
  return /[A-Za-z]/.test(ch);
}

/** Paint the correction with the same letter-case the user typed. */
function matchDomainCase(typedRaw: string, suggested: string): string {
  const typed = cleanDomainKeepCase(typedRaw);
  const typedLetters = typed.replace(/[^A-Za-z]/g, "");
  if (!typedLetters) return suggested;
  if (typedLetters === typedLetters.toUpperCase()) {
    return suggested.toUpperCase();
  }
  if (typedLetters === typedLetters.toLowerCase()) {
    return suggested;
  }

  let ti = 0;
  let out = "";
  for (let si = 0; si < suggested.length; si++) {
    const sc = suggested.charAt(si);
    while (
      ti < typed.length &&
      !isLetter(typed.charAt(ti)) &&
      typed.charAt(ti) !== "." &&
      sc !== "."
    ) {
      ti++;
    }
    const tc = ti < typed.length ? typed.charAt(ti) : "";
    if (isLetter(sc) && isLetter(tc)) {
      out += tc === tc.toUpperCase() ? sc.toUpperCase() : sc;
      ti++;
    } else if (sc === ".") {
      out += ".";
      if (tc === ".") ti++;
    } else {
      let sample = "";
      for (let k = ti; k < typed.length; k++) {
        if (isLetter(typed.charAt(k))) {
          sample = typed.charAt(k);
          break;
        }
      }
      if (!sample) {
        for (let k = out.length - 1; k >= 0; k--) {
          if (isLetter(out.charAt(k))) {
            sample = out.charAt(k);
            break;
          }
        }
      }
      out += sample && sample === sample.toUpperCase() ? sc.toUpperCase() : sc;
    }
  }
  return out;
}

function parseEmail(
  raw: string
): {
  local: string;
  domain: string;
  domainRaw: string;
  trimmed: string;
} | null {
  const trimmed = sanitizeEmailInput(raw);
  const at = trimmed.lastIndexOf("@");
  if (at <= 0 || at === trimmed.length - 1) return null;
  const local = trimmed.slice(0, at);
  const domainRaw = cleanDomainKeepCase(trimmed.slice(at + 1));
  const domain = domainRaw.toLowerCase();
  if (!local || !domain) return null;
  return { local, domain, domainRaw, trimmed: `${local}@${domainRaw}` };
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const v0 = new Array<number>(b.length + 1);
  const v1 = new Array<number>(b.length + 1);
  for (let i = 0; i <= b.length; i++) v0[i] = i;
  for (let i = 0; i < a.length; i++) {
    v1[0] = i + 1;
    for (let j = 0; j < b.length; j++) {
      const cost = a[i] === b[j] ? 0 : 1;
      v1[j + 1] = Math.min(v1[j] + 1, v0[j + 1] + 1, v0[j] + cost);
    }
    for (let j = 0; j <= b.length; j++) v0[j] = v1[j];
  }
  return v0[b.length]!;
}

function closestSld(sld: string): string | null {
  // Short SLDs (aol, msn, me, sky) false-positive too easily.
  if (sld.length < 5) return null;
  const max = sld.length <= 6 ? 1 : 2;
  let best: string | null = null;
  let bestDist = max + 1;
  for (const candidate of KNOWN_SLDS) {
    if (candidate.length < 5) continue;
    const d = levenshtein(sld, candidate);
    if (d > 0 && d < bestDist) {
      best = candidate;
      bestDist = d;
    }
  }
  return bestDist <= max ? best : null;
}

function resolveDomain(domain: string): string | null {
  if (SOFT_HINTS[domain]) return SOFT_HINTS[domain];
  if (KNOWN_DOMAINS.has(domain)) return null;
  if (DOMAIN_TYPOS[domain]) return DOMAIN_TYPOS[domain];

  const { sld, tld } = splitDomain(domain);

  const fixedTld = tld ? TLD_TYPOS[tld] : undefined;
  if (fixedTld) {
    const candidate = `${sld}.${fixedTld}`;
    if (KNOWN_DOMAINS.has(candidate)) return candidate;
    // Unknown company + impossible TLD (e.g. acme.ocm → acme.com)
    return candidate;
  }

  const mappedSld = SLD_TYPOS[sld] || closestSld(sld);
  if (mappedSld) {
    const allowed = TLDS_BY_SLD[mappedSld];
    if (tld && allowed?.has(tld)) return `${mappedSld}.${tld}`;
    return CANONICAL_BY_SLD[mappedSld] || `${mappedSld}.com`;
  }

  // Known provider, unsupported / made-up TLD (gmail.ie, outlook.xyz)
  const allowed = TLDS_BY_SLD[sld];
  if (allowed && tld && !allowed.has(tld)) {
    return CANONICAL_BY_SLD[sld] || `${sld}.com`;
  }

  return null;
}

function stillTyping(typedDomain: string, suggestedDomain: string): boolean {
  return (
    suggestedDomain.startsWith(typedDomain) && typedDomain !== suggestedDomain
  );
}

/** True when the value looks finished enough to hint (or validate). */
export function emailLooksComplete(email: string): boolean {
  const parsed = parseEmail(email);
  if (!parsed) return false;
  const { domain } = parsed;
  if (
    DOMAIN_TYPOS[domain] ||
    SOFT_HINTS[domain] ||
    KNOWN_DOMAINS.has(domain)
  ) {
    return true;
  }
  if (!domain.includes(".")) return domain.length >= 6;
  const { tld } = splitDomain(domain);
  return tld.length >= 2;
}

/**
 * Returns a correction if the domain looks like a common typo.
 * Always optional — never use this to block submit.
 */
export function suggestEmailCorrection(
  email: string,
  options: SuggestEmailOptions = {}
): EmailSuggestion | null {
  const parsed = parseEmail(email);
  if (!parsed) return null;

  const suggestedLower = resolveDomain(parsed.domain);
  if (!suggestedLower || suggestedLower === parsed.domain) return null;

  if (!options.committed && stillTyping(parsed.domain, suggestedLower)) {
    return null;
  }

  const suggestedDomain = matchDomainCase(parsed.domainRaw, suggestedLower);
  return {
    original: parsed.trimmed,
    suggested: `${parsed.local}@${suggestedDomain}`,
    originalDomain: parsed.domainRaw,
    suggestedDomain,
  };
}
